import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  registerTSPayPayment,
  suggestIbanFromDeadlines
} from "../../../store/actions/tspay";
import Page from "../../../components/general/page";
import TsPayPaymentPage from "../../../components/tspay/payment";
import moment from "moment";
import uuidv4 from "uuid/v4";
import {
  displayErrorToast,
  displayWarningToast
} from "../../../store/actions/overflowNotification";
import { temporaryGetPartyById } from "../../../store/actions/parties";
import { calcReason, getFirstTotal } from "./utils";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";

const TSPayPaymentView = props => {
  const { pageRef } = props;

  const dispatch = useDispatch();
  const location = useLocation();
  const auth = useSelector(state => state.auth);
  const main = useSelector(state => state.main);

  /* Variabili per le selezioni di data, conto bancario e beneficiario, eventualmente anche il contatto di rubrica selezionato */
  const [selectedDate, setSelectedDate] = useState(new moment());
  const [selectedBankAccount, setSelectedBankAccount] = useState();
  const [selectedBeneficiary, setSelectedBeneficiary] = useState({
    id: undefined,
    name: undefined,
    iban: undefined
  });
  const [selectedParty, setSelectedParty] = useState();

  /* Variabili per lo stato del pagamento, la risposta dell'api con url SCA e id utile per la chiamata finale dopo il feedback */
  const [SCAManagerStatus, setSCAManagerStatus] = useState(null);
  const [paymentResponse, setPaymentResponse] = useState(null);

  /* Causale */
  const [reason, setReason] = useState("");
  /* Scadenze selezionate (in caso di pagamento da fattura con più scadenze)
  e importo da pagare, utilizzato solo a video, ma mai mandato alle api di pagamento */
  const [deadlineSelected, setDeadlineSelected] = useState([]);
  const [totalAmount, setTotalAmount] = useState(getFirstTotal(location));
  /* Iban modificato manualmente, preso in considerazione solo se valorizzato, altrimenti prendo quello del beneficiario o xml */
  const [modifiedIban, setModifiedIban] = useState(undefined);
  /* Uuid univoco usato per i controlli nelle postMessage */
  const [uuidToCheck] = useState(uuidv4());

  // Flag che ci serve per switchare tra pagamento scadenza e pagamento fattura
  const fromDocuments =
    location.state && location.state.from === "documents";

  let itemsToPay;
  let paymentType;
  
  if (location.state) {
    if (location.state.deadlines) {
      paymentType = "deadlines";
      itemsToPay = location.state.deadlines;
    }
  }

  // Calcolo iniziale della causale in automatico dalle scadenze presenti
  useEffect(() => {
    if (itemsToPay) {
      setReason(calcReason(itemsToPay));
    }
  }, []);

  // Evento di lettura della postMessage (dopo una callback a pagamento completato)
  useEffect(() => {
    const handler = event => {
      if (event.data) {
        checkUuidTransaction(event);
      }
    };

    window.addEventListener("message", handler);

    // clean up
    return () => window.removeEventListener("message", handler);
  }, []);

  // Se presente un partyId in una delle scadenze, carico il beneficiario automaticamente dai parties.
  useEffect(() => {
    let counterpartWithParty = "";
    let tmpItems = [];
    if (itemsToPay) {
      itemsToPay.map(it => {
        return tmpItems.push(it.id);
      });
      counterpartWithParty = itemsToPay.find(el =>
        el.counterpart &&
          el.counterpart.partyId &&
          el.counterpart.partyId !== "");
      if (counterpartWithParty) {
        dispatch(temporaryGetPartyById(
          auth,
          main.companyUuidSelected,
          counterpartWithParty.counterpart.partyId
        ))
          .then(res => {
            setSelectedParty(res);
            setSelectedBeneficiary({
              id: res.id,
              name: res.description,
              iban:
                res.bankAccounts &&
                res.bankAccounts[0] &&
                res.bankAccounts[0].iban,
              from: "parties"
            });
          })
          .catch(() => {
            callSuggestIban(tmpItems);
          });
      } else {
        callSuggestIban(tmpItems);
      }
    }
  }, []);

  // Al cambiamento del nome del beneficiario, faccio un REGEXP per rimuovere eventuali caratteri non accettati dagli istituti bancari
  useEffect(() => {
    if (selectedBeneficiary.name) {
      setSelectedBeneficiary({
        ...selectedBeneficiary,
        name: selectedBeneficiary.name
          .replace(/[^A-Za-z0-9*/?:().,'+\- ]/g, "")
          .replace("  ", " ")
      });
    }
  }, [selectedBeneficiary.name]);

  // Chiamata del servizio suggestIban, che cerca eventuali iban negli xml delle scadenze da pagare
  const callSuggestIban = tmpItems => {
    dispatch(suggestIbanFromDeadlines(
      auth,
      main.accountantUuidSelected,
      main.companyUuidSelected,
      tmpItems
    )).then(res => {
      if (res.iban && !selectedBeneficiary.iban) {
        if (
          itemsToPay[0] &&
          itemsToPay[0].counterpart &&
          itemsToPay[0].counterpart.description
        ) {
          setSelectedBeneficiary({
            id: uuidv4(),
            name: itemsToPay[0].counterpart.description,
            iban: res.iban,
            from: "xml"
          });
        }
      }
    });
  };

  // Controllo del transactionUuid dopo postMessage, per aggiornamento maschera e feedback pagamento.
  const checkUuidTransaction = event => {
    if (
      event.data.transactionUuid &&
      event.data.transactionUuid === uuidToCheck
    ) {
      let itemsToPayArr = [];
      itemsToPay.map(it => {
        return itemsToPayArr.push(it.id);
      });
      // dispatch(displaySuccessToast("Pagamento con TS Pay completato"));
      setSCAManagerStatus("done");
    }
  };

  // Evento al click del pulsante "Paga"
  const onPaymentButtonClick = () => {
    // Controllo causale
    if (!reason || (reason && reason.trim().length < 5)) {
      dispatch(displayWarningToast(<FormattedMessage id="tspay.payment.warnings.minReason" />));
      return;
    }
    let itemsToPayArr = [];
    itemsToPay.map(it => {
      return itemsToPayArr.push(it.id);
    });
    setPaymentResponse(null);
    setSCAManagerStatus("waiting");
    dispatch(registerTSPayPayment(
      auth,
      main.accountantUuidSelected,
      main.companyUuidSelected,
      { name: selectedBankAccount.name, iban: selectedBankAccount.iban },
      {
        name: selectedBeneficiary.name,
        iban:
            modifiedIban && modifiedIban !== ""
              ? modifiedIban
              : selectedBeneficiary.iban
      },
      fromDocuments ? deadlineSelected : itemsToPayArr,
      uuidToCheck,
      reason
    ))
      .then(res => {
        // Importante per il root, che si occuperà, a fine pagamento, di chiamare le api e impostare le scadenze pagate in "pending"
        localStorage.setItem(`tspayPaymentUuidToCheck_${uuidToCheck}`,
          JSON.stringify({
            deadlineIds: itemsToPayArr,
            initDeadlinesPayId: res.initDeadlinesPayId
          }));
        setPaymentResponse(res);
        setSCAManagerStatus("paying");
      })
      .catch(err => {
        if (err.response) {
          dispatch(displayErrorToast(err.response.data.message));
        }
        setSCAManagerStatus(null);
      });
  };

  return (
    <Page pageRef={pageRef} noHeaderContent title="Pagamento Ts Pay">
      <TsPayPaymentPage
        selectedParty={selectedParty}
        setSelectedParty={setSelectedParty}
        selectedBeneficiary={selectedBeneficiary}
        setSelectedBeneficiary={setSelectedBeneficiary}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedBankAccount={selectedBankAccount}
        setSelectedBankAccount={setSelectedBankAccount}        
        onPaymentButtonClick={onPaymentButtonClick}
        itemsToPay={itemsToPay}
        paymentType={paymentType}
        SCAManagerStatus={SCAManagerStatus}
        paymentResponse={paymentResponse}
        uuidToCheck={uuidToCheck}
        reason={reason}
        setReason={setReason}
        deadlineSelected={deadlineSelected}
        setDeadlineSelected={setDeadlineSelected}
        totalAmount={totalAmount}
        setTotalAmount={setTotalAmount}
        fromDocuments={fromDocuments}
        modifiedIban={modifiedIban}
        setModifiedIban={setModifiedIban}
      />
    </Page>
  );
};

export default TSPayPaymentView;
